import React, { useContext, useEffect, useRef, useState } from "react";
import PropTypes from "prop-types";
import { Button, message, Popconfirm, Row, Select, Space, Switch } from "antd";
import { CircleAnimationCompact } from "../components/loading";
import { WaitingForACompany } from "../components/WaitingForACompany";

import {
	EyeInvisibleOutlined,
	EyeOutlined,
	PlusOutlined,
	QuestionCircleOutlined,
	SaveOutlined,
	UndoOutlined,
} from "@ant-design/icons";

import {	
	HorizontalBoard,
	CreateCategoryModal,
	ClassificationAction,
	useClassificationsStateHook,
	useClassificationOperations,
	useClassificationActions,
	useSaveClassifications,
} from "../components/classifications";

import { ClassificationsContext } from "../contexts/ClassificationsContext";

export const Classifications = ({ token, company }) => {
	const createCategoryModalRef = useRef(null);
	const [showArchived, setShowArchived] = useState(false);
	const [fetchedCompanyId, setFetchedCompanyId] = useState(null);

	const {
		classifications: backupData,
		setClassifications: setBackupData,
		fetchClassifications,
	} = useContext(ClassificationsContext);
	
	const operations = useClassificationOperations();

	const {
		data,
		updateData,
		pendingOperations,
		addPendingOperation,
		clearPendingOperations,
		selectedItems,
		toggleSelection,
		handleSelectWithChildren,
		handleClassificationEdits,
		handleClassificationOrder,
		getSelectedIds,
		setSelectedItems,
	} = useClassificationsStateHook([]);

	const {
		selectedAction,
		setSelectedAction,
		resetData: resetDataAction,
		handleCreateCategory,
		handleActionSelected,
	} = useClassificationActions(
		data,
		updateData,
		addPendingOperation,
		clearPendingOperations,
		getSelectedIds,
		setSelectedItems,
		operations
	);

	const { loading, setLoading, saveChanges } = useSaveClassifications(
		token,
		data,
		updateData,
		pendingOperations,
		clearPendingOperations,
		setBackupData,
		operations.cleanupData
	);

	// Cargar datos iniciales
	useEffect(() => {
		const fetchData = async () => {
			const classificationToSet = await fetchClassifications(
				company.id,
				setLoading
			);
			updateData(classificationToSet);
			setFetchedCompanyId(company.id);
		};

		// if (company.id === "" || data.length > 0) return;
		if (company.id === "" || fetchedCompanyId === company.id) return;
		
		fetchData();
	}, [company, fetchClassifications, updateData, data.length]);

	useEffect(() => { // Reset data si cambió de empresa
		updateData([]);
		setFetchedCompanyId(null);
	}, [company.id]);

	// Métodos simplificados
	const resetData = () => resetDataAction(backupData);
	const handleSaveChanges = () => saveChanges(company.id);

	// Renderizado condicional de la vista de clasificaciones
	const ClassificationsView = (
		<>
			<Row justify="end" style={{ marginBottom: 16 }}>
				<Space>
					<Switch
						checkedChildren={<EyeOutlined />}
						unCheckedChildren={<EyeInvisibleOutlined />}
						checked={showArchived}
						onChange={(checked) => {
							setShowArchived(checked);
							message.info(
								checked
									? "Mostrando Archivados"
									: "Ocultando Archivados"
							);
						}}
					/>
					<Select
						value={selectedAction}
						onChange={(value) => {
							setSelectedAction(value);
							handleActionSelected(value);
						}}
						options={[
							{
								value: ClassificationAction.EMPTY,
								label: "Acciones",
							},
							{
								value: ClassificationAction.ARCHIVE,
								label: "Archivar",
							},
							{
								value: ClassificationAction.UNARCHIVE,
								label: "Restaurar",
							},
							{
								value: ClassificationAction.DELETE,
								label: "Eliminar",
							},
						]}
						style={{ width: "175px" }}
						disabled={Object.keys(selectedItems).length === 0}
						className="custom-select"
					/>
					<Popconfirm
						title="Revertir Cambios de Clasificaciones"
						description="¿Estás seguro de que deseas revertir los cambios? Vas a perder tu proceso"
						onConfirm={resetData}
						icon={
							<QuestionCircleOutlined style={{ color: "red" }} />
						}
					>
						<Button
							icon={<UndoOutlined />}
							type="primary"
							ghost
							disabled={pendingOperations.length === 0}
						>
							Revertir Cambios
						</Button>
					</Popconfirm>
					<Button
						onClick={() =>
							createCategoryModalRef.current?.openModal()
						}
						icon={<PlusOutlined />}
						type="primary"
						ghost
					>
						Crear Categoría
					</Button>
					<Button
						onClick={handleSaveChanges}
						icon={<SaveOutlined />}
						type="primary"
						disabled={pendingOperations.length === 0}
					>
						Guardar Cambios ({pendingOperations.length})
					</Button>
				</Space>
			</Row>
			<HorizontalBoard
				initialData={data}
				selectedItems={selectedItems}
				showArchived={showArchived}
				backupData={backupData}
				onUpdateOrder={handleClassificationOrder}				
				onDeleteClassifications={(value) => addPendingOperation("DELETE", value)}
				onEditClassifications={handleClassificationEdits}
				onToggleSelection={toggleSelection}
				onSelectWithChildren={handleSelectWithChildren}
			/>
			<CreateCategoryModal
				ref={createCategoryModalRef}
				onSendCategory={handleCreateCategory}
			/>
		</>
	);

	return (
		<>
			{loading && <CircleAnimationCompact />}
			{!loading && (
				<>
					<WaitingForACompany company_id={company.id} />
					{company.id !== "" && ClassificationsView}
				</>
			)}
		</>
	);
};

Classifications.propTypes = {
	token: PropTypes.string.isRequired,
	company: PropTypes.shape({
		id: PropTypes.string.isRequired,
		tid: PropTypes.string.isRequired,
		name: PropTypes.string.isRequired,
		accounting_resources: PropTypes.object.isRequired,
	}).isRequired,
};
